import { useState } from "react";

import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { CalculatorIcon, PlusIcon, TrashIcon } from "@heroicons/react/24/outline";

import { ContactCombobox } from "../../contacts/ContactCombobox";
import useNotification from "../../notifications/useNotifications";
import { ButtonInput } from "../../shared/Inputs/ButtonInput";
import useQuoteCalculator from "../../../data/useQuoteCalculator";
import { useFeatureFlag } from "../../../hooks/useFeatureFlag";
import Modal from "../../../layouts/Modal";
import { IContact } from "../../../models/Contact";
import {
  ICalculatedQuote,
  IQuoteLeadContact,
  QuoteAcceptedAs,
  QuoteBodyCondition,
  QuoteEngineCondition,
  QuoteLeadCreateAs,
  QuotePriceType,
} from "../../../models/QuoteCalculator";
import classNames from "../../../utils/classNames";
import { formatPrice } from "../../../utils/currency";

interface QuoteCalculatorModalProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

type Step = "seller" | "vehicles" | "results";
type ContactMode = "existing" | "new";

interface NewContact {
  fullname: string;
  telephone1: string;
  email: string;
}

interface VehicleRow {
  key: number;
  vrm: string;
  engineCondition: QuoteEngineCondition;
  bodyCondition: QuoteBodyCondition;
  alloyWheels: boolean;
  mileage: string;
  /** Set once Get Quotes has run for this row. */
  quote?: ICalculatedQuote;
  error?: string;
  /** Which price the lead takes; defaults to whichever option the customer offers. */
  acceptedAs: QuoteAcceptedAs;
  creating?: boolean;
  createdLeadId?: string;
  createdAs?: QuoteLeadCreateAs;
  /** The seller declined on the call: the reason box is open, the cancel is in flight, or done. */
  cancelOpen?: boolean;
  cancelReason?: string;
  cancelling?: boolean;
  cancelled?: boolean;
}

const inputClass = "w-full mt-1 text-gray-900 border-1 border-gray-300 px-3 py-2 text-sm";
const labelClass = "text-sm font-medium text-gray-500";
const secondaryButton = "bg-white !text-gray-700 border !border-gray-300 hover:!bg-gray-50";

let nextRowKey = 1;

const emptyRow = (): VehicleRow => ({
  key: nextRowKey++,
  vrm: "",
  engineCondition: QuoteEngineCondition.NoFault,
  bodyCondition: QuoteBodyCondition.Good,
  alloyWheels: false,
  mileage: "",
  acceptedAs: QuoteAcceptedAs.Collection,
});

/**
 * Get quotes without leaving Wilma: who the seller is, then one or more vehicles at a postcode,
 * priced by the widget for this customer. With a seller given, every priced vehicle is saved as
 * soon as its prices come back: as a quote on the call list for a call centre, as a lead for
 * anyone else. With the seller skipped, prices are only shown and nothing is written.
 */
export default function QuoteCalculatorModal({ open, setOpen }: QuoteCalculatorModalProps) {
  const { t } = useTranslation("leads");
  const { addNotification } = useNotification();
  const { calculate, createLead, cancelLead } = useQuoteCalculator("QuoteCalculatorModal");
  const isCallCentre = useFeatureFlag("Call Centre");

  const [step, setStep] = useState<Step>("seller");
  const [contactMode, setContactMode] = useState<ContactMode>("existing");
  const [existingContact, setExistingContact] = useState<IContact | undefined>(undefined);
  const [newContact, setNewContact] = useState<NewContact>({ fullname: "", telephone1: "", email: "" });
  const [postcode, setPostcode] = useState("");
  const [rows, setRows] = useState<VehicleRow[]>([emptyRow()]);
  const [officeNotes, setOfficeNotes] = useState("");
  const [pricing, setPricing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const leadLabel = isCallCentre ? t("Booking") : t("Lead");

  const hasSeller =
    contactMode === "existing"
      ? !!existingContact
      : newContact.fullname.trim().length > 0 && (newContact.telephone1.trim().length > 0 || newContact.email.trim().length > 0);

  const sellerName = contactMode === "existing" ? existingContact?.fullname : newContact.fullname;

  const contactPayload = (): IQuoteLeadContact =>
    contactMode === "existing"
      ? { contactId: existingContact?.id }
      : { fullname: newContact.fullname.trim(), telephone1: newContact.telephone1.trim(), email: newContact.email.trim() };

  const close = () => {
    setOpen(false);
    setStep("seller");
    setContactMode("existing");
    setExistingContact(undefined);
    setNewContact({ fullname: "", telephone1: "", email: "" });
    setPostcode("");
    setRows([emptyRow()]);
    setOfficeNotes("");
    setError(null);
  };

  const updateRow = (key: number, patch: Partial<VehicleRow>) =>
    setRows((current) => current.map((row) => (row.key === key ? { ...row, ...patch } : row)));

  const pickContact = (contact: IContact) => {
    setExistingContact(contact);
    if (!postcode && contact.postcode) setPostcode(contact.postcode);
  };

  const continueFromSeller = (skip: boolean) => {
    if (!skip && !hasSeller) {
      setError(t("Pick a contact, or give a name and a phone number or email."));
      return;
    }
    setError(null);
    setStep("vehicles");
  };

  const priceTypeLabel = (type: QuotePriceType | null) => {
    switch (type) {
      case QuotePriceType.Salvage:
        return t("Salvage");
      case QuotePriceType.SmartPrice:
        return t("Smart price");
      case QuotePriceType.ScrapExport:
        return t("Scrap / export");
      default:
        return "";
    }
  };

  // What a priced vehicle becomes when it is saved: a call centre works it as a quote on the call
  // list, anyone else gets a lead.
  const defaultCreateAs = isCallCentre ? QuoteLeadCreateAs.Quote : QuoteLeadCreateAs.Lead;

  const saveRow = async (row: VehicleRow, createAs: QuoteLeadCreateAs): Promise<VehicleRow> => {
    if (!row.quote) return row;

    try {
      const response = await createLead.mutateAsync({
        quoteId: row.quote.quoteId,
        body: {
          acceptedAs: row.acceptedAs,
          createAs,
          contact: contactPayload(),
          officeNotes,
        },
      });
      return { ...row, creating: false, createdLeadId: response.data.id, createdAs: createAs, error: undefined };
    } catch (err: any) {
      return { ...row, creating: false, error: err?.message || t("The lead could not be created.") };
    }
  };

  const getQuotes = async () => {
    const toPrice = rows.filter((row) => row.vrm.trim().length > 0);

    if (!postcode.trim()) {
      setError(t("A postcode is needed to get a quote."));
      return;
    }
    if (toPrice.length === 0) {
      setError(t("Enter at least one registration."));
      return;
    }

    setError(null);
    setPricing(true);

    // Each vehicle is one widget quote, priced independently so one unknown registration does
    // not stop the rest. With a seller given it is saved straight away, so the record exists in
    // Wilma the moment the price is on screen.
    const priced = await Promise.all(
      toPrice.map(async (row): Promise<VehicleRow> => {
        const mileage = Number(row.mileage);
        try {
          const response = await calculate.mutateAsync({
            vrm: row.vrm,
            postcode,
            engineCondition: row.engineCondition,
            bodyCondition: row.bodyCondition,
            alloyWheels: row.alloyWheels,
            mileage: Number.isFinite(mileage) && mileage > 0 ? Math.round(mileage) : 0,
          });
          const quote = response.data;
          const pricedRow: VehicleRow = {
            ...row,
            quote,
            error: undefined,
            acceptedAs: quote.priceCollected !== null ? QuoteAcceptedAs.Collection : QuoteAcceptedAs.DropOff,
          };
          const offered = quote.priceCollected !== null || quote.priceDropOff !== null;
          return hasSeller && offered ? await saveRow(pricedRow, defaultCreateAs) : pricedRow;
        } catch (err: any) {
          return { ...row, quote: undefined, error: err?.message || t("The widget could not price this vehicle.") };
        }
      })
    );

    const saved = priced.filter((row) => row.createdLeadId).length;
    if (saved > 0) {
      addNotification({
        variant: "success",
        primaryText:
          defaultCreateAs === QuoteLeadCreateAs.Quote
            ? t("{{count}} quote(s) saved", { count: saved })
            : t("{{count}} {{label}}(s) created", { count: saved, label: leadLabel.toLowerCase() }),
      });
    }

    setRows(priced);
    setPricing(false);
    setStep("results");
  };

  const create = async (row: VehicleRow, createAs: QuoteLeadCreateAs) => {
    if (!row.quote) return;

    if (!hasSeller) {
      setError(t("Add the seller before creating anything."));
      setStep("seller");
      return;
    }

    setError(null);
    updateRow(row.key, { creating: true });

    const result = await saveRow(row, createAs);
    updateRow(row.key, result);

    if (result.createdLeadId) {
      addNotification({
        variant: "success",
        primaryText: createAs === QuoteLeadCreateAs.Quote ? t("Quote saved for {{vrm}}", { vrm: row.quote.vrm }) : t("{{label}} created for {{vrm}}", { label: leadLabel, vrm: row.quote.vrm }),
      });
    } else if (result.error) {
      setError(result.error);
    }
  };

  // The seller said no while the modal is still open: withdraw the record before closing, so
  // nothing is left on the call list or in Awaiting Contact for someone else to chase.
  const cancelRow = async (row: VehicleRow) => {
    if (!row.createdLeadId) return;

    setError(null);
    updateRow(row.key, { cancelling: true });

    try {
      await cancelLead.mutateAsync({ leadId: row.createdLeadId, body: { reason: row.cancelReason?.trim() || undefined } });
      updateRow(row.key, { cancelling: false, cancelled: true, cancelOpen: false });
      addNotification({ variant: "info", primaryText: t("Cancelled {{vrm}}", { vrm: row.quote?.vrm ?? row.vrm }) });
    } catch (err: any) {
      updateRow(row.key, { cancelling: false });
      setError(err?.message || t("The record could not be cancelled."));
    }
  };

  const createdLabel = (row: VehicleRow) =>
    row.createdAs === QuoteLeadCreateAs.Quote ? t("Quote saved") : t("{{label}} created", { label: leadLabel });

  const leadPath = (row: VehicleRow) => {
    if (!row.createdLeadId) return "";
    if (row.createdAs === QuoteLeadCreateAs.Quote && isCallCentre) return "/quotes";
    return `${isCallCentre ? "/bookings" : "/leads"}/${row.createdLeadId}`;
  };

  const stepTitles: Record<Step, string> = {
    seller: t("1. Seller"),
    vehicles: t("2. Vehicles"),
    results: t("3. Prices"),
  };

  return (
    <Modal open={open} setOpen={(value) => (value ? setOpen(true) : close())} width="sm:max-w-4xl">
      <div className="flex items-center gap-x-2">
        <CalculatorIcon className="h-6 w-6 text-primary-600" aria-hidden="true" />
        <h3 className="text-lg font-medium leading-6 text-gray-900">{t("Get a quote")}</h3>
      </div>
      <p className="mt-1 text-sm text-gray-500">
        {t("Priced by your website widget. Nothing is saved in Wilma until you save a quote or create a {{label}}.", { label: leadLabel.toLowerCase() })}
      </p>

      <nav className="mt-4 flex gap-1 rounded-lg bg-gray-100 p-1 text-[13px] font-medium" aria-label={t("Steps")}>
        {(Object.keys(stepTitles) as Step[]).map((s) => (
          <span
            key={s}
            aria-current={step === s ? "step" : undefined}
            className={classNames("rounded-md px-3 py-1.5", step === s ? "bg-primary-600 text-white" : "text-gray-500")}
          >
            {stepTitles[s]}
          </span>
        ))}
      </nav>

      {step === "seller" && (
        <div className="mt-4">
          <div className="flex items-center justify-between">
            <label className={labelClass}>{t("Who is asking?")}</label>
            <div className="flex rounded-md border border-gray-300 text-xs">
              <button
                type="button"
                onClick={() => setContactMode("existing")}
                className={classNames("rounded-l-md px-2 py-1", contactMode === "existing" ? "bg-primary-600 text-white" : "bg-white text-gray-700")}
              >
                {t("Existing contact")}
              </button>
              <button
                type="button"
                onClick={() => setContactMode("new")}
                className={classNames("rounded-r-md px-2 py-1", contactMode === "new" ? "bg-primary-600 text-white" : "bg-white text-gray-700")}
              >
                {t("New contact")}
              </button>
            </div>
          </div>

          {contactMode === "existing" ? (
            <div className="mt-2">
              <ContactCombobox onContactSelect={pickContact} rounded />
              {existingContact && (
                <p className="mt-1 text-xs text-gray-500">
                  {existingContact.fullname}
                  {existingContact.telephone1 ? ` · ${existingContact.telephone1}` : ""}
                  {existingContact.postcode ? ` · ${existingContact.postcode}` : ""}
                </p>
              )}
            </div>
          ) : (
            <div className="mt-2 grid grid-cols-1 gap-x-4 gap-y-3 sm:grid-cols-3">
              <div>
                <label className={labelClass}>{t("Name")}<span className="text-primary-500">*</span></label>
                <input className={inputClass} value={newContact.fullname} onChange={(e) => setNewContact({ ...newContact, fullname: e.target.value })} />
              </div>
              <div>
                <label className={labelClass}>{t("Phone")}</label>
                <input className={inputClass} value={newContact.telephone1} onChange={(e) => setNewContact({ ...newContact, telephone1: e.target.value })} />
              </div>
              <div>
                <label className={labelClass}>{t("Email")}</label>
                <input className={inputClass} type="email" value={newContact.email} onChange={(e) => setNewContact({ ...newContact, email: e.target.value })} />
              </div>
            </div>
          )}

          <p className="mt-3 text-xs text-gray-500">
            {isCallCentre
              ? t("Each vehicle is saved as a quote for this seller as soon as it is priced. Skip to only see prices.")
              : t("Each vehicle is saved as a lead for this seller as soon as it is priced. Skip to only see prices.")}
          </p>

          {error && <p className="mt-4 text-sm text-red-600">{error}</p>}

          <div className="mt-6 flex justify-end gap-x-2">
            <ButtonInput label={t("Cancel")} isSubmit={false} onClick={close} classes={secondaryButton} />
            <ButtonInput label={t("Just show prices")} isSubmit={false} onClick={() => continueFromSeller(true)} classes={secondaryButton} />
            <ButtonInput label={t("Continue")} isSubmit={false} onClick={() => continueFromSeller(false)} classes="" />
          </div>
        </div>
      )}

      {step === "vehicles" && (
        <div className="mt-4">
          <div className="grid grid-cols-1 gap-x-4 sm:grid-cols-3">
            <div>
              <label className={labelClass}>{t("Postcode")}<span className="text-primary-500">*</span></label>
              <input className={inputClass} value={postcode} onChange={(e) => setPostcode(e.target.value.toUpperCase())} />
            </div>
            <p className="self-end pb-2 text-xs text-gray-500 sm:col-span-2">
              {t("Prices are set per area, so all vehicles here are priced for this postcode.")}
            </p>
          </div>

          <div className="mt-4 overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                  <th className="pb-1 pr-2">{t("Registration")}</th>
                  <th className="pb-1 pr-2">{t("Engine")}</th>
                  <th className="pb-1 pr-2">{t("Body")}</th>
                  <th className="pb-1 pr-2">{t("Alloys")}</th>
                  <th className="pb-1 pr-2">{t("Mileage")}</th>
                  <th className="pb-1" />
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.key}>
                    <td className="pr-2 align-top">
                      <input
                        className={classNames(inputClass, "uppercase")}
                        placeholder="e.g. KT68XVX"
                        value={row.vrm}
                        onChange={(e) => updateRow(row.key, { vrm: e.target.value.toUpperCase() })}
                      />
                    </td>
                    <td className="pr-2 align-top">
                      <select className={inputClass} value={row.engineCondition} onChange={(e) => updateRow(row.key, { engineCondition: Number(e.target.value) })}>
                        <option value={QuoteEngineCondition.NoFault}>{t("No fault")}</option>
                        <option value={QuoteEngineCondition.MinorFault}>{t("Minor fault")}</option>
                        <option value={QuoteEngineCondition.MajorFault}>{t("Major fault")}</option>
                      </select>
                    </td>
                    <td className="pr-2 align-top">
                      <select className={inputClass} value={row.bodyCondition} onChange={(e) => updateRow(row.key, { bodyCondition: Number(e.target.value) })}>
                        <option value={QuoteBodyCondition.Good}>{t("Good")}</option>
                        <option value={QuoteBodyCondition.Average}>{t("Average")}</option>
                        <option value={QuoteBodyCondition.HeavyDamage}>{t("Heavy damage")}</option>
                      </select>
                    </td>
                    <td className="pr-2 pt-3 text-center align-top">
                      <input type="checkbox" className="border-1 border-gray-300" checked={row.alloyWheels} onChange={(e) => updateRow(row.key, { alloyWheels: e.target.checked })} />
                    </td>
                    <td className="pr-2 align-top">
                      <input className={inputClass} type="number" min={0} step={1} placeholder={t("Last known")} value={row.mileage} onChange={(e) => updateRow(row.key, { mileage: e.target.value })} />
                    </td>
                    <td className="pt-2 align-top">
                      <button
                        type="button"
                        onClick={() => setRows((current) => (current.length > 1 ? current.filter((r) => r.key !== row.key) : current))}
                        disabled={rows.length === 1}
                        className="rounded-md p-1 text-gray-400 hover:text-red-600 disabled:opacity-30"
                        title={t("Remove")}
                      >
                        <TrashIcon className="h-5 w-5" aria-hidden="true" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <button
            type="button"
            onClick={() => setRows((current) => [...current, emptyRow()])}
            className="mt-2 inline-flex items-center gap-x-1 text-sm font-medium text-primary-600 hover:text-primary-700"
          >
            <PlusIcon className="h-4 w-4" aria-hidden="true" />
            {t("Add another vehicle")}
          </button>

          {hasSeller && (
            <div className="mt-4">
              <label className={labelClass}>{t("Office notes")}</label>
              <input className={inputClass} value={officeNotes} onChange={(e) => setOfficeNotes(e.target.value)} placeholder={t("Written on every record saved from here")} />
            </div>
          )}

          {error && <p className="mt-4 text-sm text-red-600">{error}</p>}

          <div className="mt-6 flex justify-end gap-x-2">
            <ButtonInput label={t("Back")} isSubmit={false} onClick={() => setStep("seller")} classes={secondaryButton} />
            <ButtonInput
              label={
                !hasSeller
                  ? t("Get Prices")
                  : defaultCreateAs === QuoteLeadCreateAs.Quote
                    ? t("Get Prices & Save Quotes")
                    : t("Get Prices & Create {{label}}s", { label: leadLabel })
              }
              isSubmit={false}
              onClick={getQuotes}
              isLoading={pricing}
              classes=""
            />
          </div>
        </div>
      )}

      {step === "results" && (
        <div className="mt-4">
          <div className="rounded-md border border-gray-200 bg-gray-50 px-4 py-2 text-sm text-gray-700">
            {hasSeller ? (
              <span><span className="font-semibold">{sellerName}</span> · {postcode}</span>
            ) : (
              <span>
                {t("No seller yet")} · {postcode} ·{" "}
                <button type="button" onClick={() => setStep("seller")} className="font-medium text-primary-600 hover:text-primary-700">
                  {t("Add seller")}
                </button>
              </span>
            )}
          </div>

          <div className="mt-4 overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-xs font-medium uppercase tracking-wide text-gray-500">
                  <th className="pb-1 pr-3">{t("Vehicle")}</th>
                  <th className="pb-1 pr-3">{t("Collection")}</th>
                  <th className="pb-1 pr-3">{t("Drop off")}</th>
                  <th className="pb-1 pr-3">{t("Accepted as")}</th>
                  <th className="pb-1" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.filter((row) => row.quote || row.error).map((row) => (
                  <tr key={row.key} className="align-top">
                    <td className="py-2 pr-3">
                      <div className="font-semibold">{row.quote?.vrm ?? row.vrm}</div>
                      {row.quote ? (
                        <div className="text-xs text-gray-500">
                          {[row.quote.make, row.quote.model].filter(Boolean).join(" ")}
                          {row.quote.year ? ` · ${row.quote.year}` : ""}
                          {row.quote.mileage ? ` · ${row.quote.mileage.toLocaleString()} ${t("miles")}` : ""}
                        </div>
                      ) : (
                        <div className="text-xs text-red-600">{row.error}</div>
                      )}
                    </td>
                    <td className="py-2 pr-3">
                      {row.quote && <Price price={row.quote.priceCollected} detail={priceTypeLabel(row.quote.collectedType)} unavailable={t("Not offered")} />}
                    </td>
                    <td className="py-2 pr-3">
                      {row.quote && <Price price={row.quote.priceDropOff} detail={priceTypeLabel(row.quote.dropOffType)} unavailable={t("Not offered")} />}
                    </td>
                    <td className="py-2 pr-3">
                      {row.quote && !row.createdLeadId && (row.quote.priceCollected !== null || row.quote.priceDropOff !== null) && (
                        <select className={classNames(inputClass, "mt-0")} value={row.acceptedAs} onChange={(e) => updateRow(row.key, { acceptedAs: Number(e.target.value) })}>
                          {row.quote.priceCollected !== null && <option value={QuoteAcceptedAs.Collection}>{t("Collection")}</option>}
                          {row.quote.priceDropOff !== null && <option value={QuoteAcceptedAs.DropOff}>{t("Drop off")}</option>}
                        </select>
                      )}
                    </td>
                    <td className="py-2">
                      {row.cancelled ? (
                        <span className="text-sm text-gray-400">{t("Cancelled")}</span>
                      ) : row.createdLeadId && row.cancelOpen ? (
                        <div className="flex flex-col gap-1">
                          <input
                            className={classNames(inputClass, "mt-0")}
                            placeholder={t("Why did they decline? (optional)")}
                            value={row.cancelReason ?? ""}
                            onChange={(e) => updateRow(row.key, { cancelReason: e.target.value })}
                          />
                          <div className="flex gap-1">
                            <ButtonInput label={t("Keep")} isSubmit={false} size="sm" classes={secondaryButton} onClick={() => updateRow(row.key, { cancelOpen: false })} />
                            <ButtonInput label={t("Confirm cancel")} isSubmit={false} size="sm" classes="!bg-red-600 hover:!bg-red-700" isLoading={!!row.cancelling} onClick={() => cancelRow(row)} />
                          </div>
                        </div>
                      ) : row.createdLeadId ? (
                        <div className="flex flex-col gap-1">
                          <Link to={leadPath(row)} onClick={close} className="text-sm font-medium text-primary-600 hover:text-primary-700">
                            {createdLabel(row)} · {t("open")}
                          </Link>
                          <button
                            type="button"
                            onClick={() => updateRow(row.key, { cancelOpen: true })}
                            className="self-start text-xs font-medium text-red-600 hover:text-red-700"
                          >
                            {t("Seller declined · cancel")}
                          </button>
                        </div>
                      ) : row.quote && (row.quote.priceCollected !== null || row.quote.priceDropOff !== null) ? (
                        <div className="flex flex-wrap gap-1">
                          {isCallCentre && (
                            <ButtonInput label={t("Save as quote")} isSubmit={false} size="sm" classes={secondaryButton} isLoading={!!row.creating} onClick={() => create(row, QuoteLeadCreateAs.Quote)} />
                          )}
                          <ButtonInput label={t("Create {{label}}", { label: leadLabel })} isSubmit={false} size="sm" classes="" isLoading={!!row.creating} onClick={() => create(row, QuoteLeadCreateAs.Lead)} />
                        </div>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {!hasSeller && (
            <p className="mt-3 text-xs text-gray-500">
              {t("Prices only. Add a seller to save any of these as a quote or {{label}}.", { label: leadLabel.toLowerCase() })}
            </p>
          )}

          {error && <p className="mt-4 text-sm text-red-600">{error}</p>}

          <div className="mt-6 flex justify-end gap-x-2">
            <ButtonInput label={t("Back")} isSubmit={false} onClick={() => setStep("vehicles")} classes={secondaryButton} />
            <ButtonInput label={t("Done")} isSubmit={false} onClick={close} classes="" />
          </div>
        </div>
      )}
    </Modal>
  );
}

function Price({ price, detail, unavailable }: { price: number | null; detail: string; unavailable: string }) {
  if (price === null) return <span className="text-sm text-gray-400">{unavailable}</span>;

  return (
    <div>
      <div className="text-base font-semibold text-gray-900">{formatPrice(price)}</div>
      {detail && <div className="text-xs text-gray-500">{detail}</div>}
    </div>
  );
}
